import Container from '@ui/container/container';
import { ICONS } from '@ui/icons/types';
import type { ResolvedEmailBlock } from '../../../templates/widgets/block-types';
import ColorPill from '../controls/color-pill';
import NumberField from '../controls/number-field';
import BorderControl from '../controls/border-control';
import BorderRadiusControl from '../controls/border-radius-control';
import BoxModelControl from '../controls/box-model-control';
import { useBoxModelStyle } from '../controls/use-box-model-style';
import { useResolvedBlockStyle } from '../controls/use-resolved-block-style';
import chroma from 'chroma-js';
import { useCallback, useRef } from 'react';

interface BlockStylePanelProps {
  block: ResolvedEmailBlock;
  onUpdateStyle: (key: string, value: unknown) => void;
  radiusLabel?: string;
}

export default function BlockStylePanel({ block, onUpdateStyle, radiusLabel = 'Corner radius' }: BlockStylePanelProps) {
  const style = block.style ?? {};

  const resolved = useResolvedBlockStyle(block.id, block.style);
  const colorChangeCallback = useRef<number>(0);

  const padding = useBoxModelStyle({
    blockId: block.id,
    styleValue: style.padding,
    resolvedValues: [resolved.paddingTop, resolved.paddingRight, resolved.paddingBottom, resolved.paddingLeft],
    styleKey: 'padding',
    uniformity: 'paired',
    onUpdateStyle,
  });

  const margin = useBoxModelStyle({
    blockId: block.id,
    styleValue: style.margin,
    resolvedValues: [resolved.marginTop, resolved.marginRight, resolved.marginBottom, resolved.marginLeft],
    styleKey: 'margin',
    uniformity: 'paired',
    onUpdateStyle,
  });

  const onBackgroundChange = useCallback(
    (v: string) => {
      onUpdateStyle('background', v);

      // updating two values at once causes a little visual jank because of computing the
      // contrast - wait until we're not in the middle of a render to run this check
      // then update the view
      cancelIdleCallback(colorChangeCallback.current);
      colorChangeCallback.current = requestIdleCallback(() => {
        let baseColor = '#ffffff';
        const element = document.querySelector(`td[data-block-id="${block.id}"] > div`);

        // get the current color of the element
        if (element instanceof HTMLElement) {
          baseColor = element.style.color;

          if (baseColor.startsWith('var(--')) {
            baseColor = getComputedStyle(element)
              .getPropertyValue(baseColor.replace(/var\(|\)/g, ''))
              .trim();
          }
        }

        const contrastRatio = chroma.contrast(baseColor, v);
        const enoughContrast = contrastRatio > 4.5;

        // if there is enough contrast, keep the current color. If not, then swap the color with either nearly black or nearly white
        onUpdateStyle('color', enoughContrast ? baseColor : chroma(v).luminance() > 0.5 ? '#000000dd' : '#ffffffdd');
      });
    },
    [block.id, onUpdateStyle]
  );

  return (
    <Container className="flex-column gap-16" padding="0 16px">
      <ColorPill
        label="Background color"
        value={style.background as string | undefined}
        isOverridden={style.background !== undefined}
        onChange={onBackgroundChange}
        onReset={() => onUpdateStyle('background', undefined)}
      />

      <BorderControl
        style={style}
        onUpdateStyle={onUpdateStyle}
        resolvedColor={resolved.borderColor}
        resolvedWidth={resolved.borderWidth}
        defaultStyleValue={resolved.hasBorder ? 'solid' : 'none'}
      />

      <BorderRadiusControl
        label={radiusLabel}
        blockId={block.id}
        style={style}
        onUpdateStyle={onUpdateStyle}
        resolvedValues={[
          resolved.cornerTopLeft,
          resolved.cornerTopRight,
          resolved.cornerBottomRight,
          resolved.cornerBottomLeft,
        ]}
        expandable
      />

      <BoxModelControl
        sectionLabel="Padding"
        expanded={padding.expanded}
        onToggleExpand={() => padding.setExpanded((prev) => !prev)}
        toggleIcon={ICONS.PADDING_SIDES}
        toggleLabel="padding controls"
        values={padding.values}
        onChangeSlot={padding.setSlot}
        fields={[
          { slot: 0, icon: ICONS.PADDING_TOP, ariaLabel: 'Padding top' },
          { slot: 1, icon: ICONS.PADDING_RIGHT, ariaLabel: 'Padding right' },
          { slot: 2, icon: ICONS.PADDING_BOTTOM, ariaLabel: 'Padding bottom' },
          { slot: 3, icon: ICONS.PADDING_LEFT, ariaLabel: 'Padding left' },
        ]}
        renderCollapsed={() => (
          <Container className="flex gap-12 ai-flex-end">
            <NumberField
              label="Padding"
              icon={ICONS.PADDING_VERTICAL}
              allowNegative={false}
              min={0}
              max={100}
              value={padding.values[0]}
              onChange={padding.setCollapsedFirst}
            />
            <NumberField
              icon={ICONS.PADDING_HORIZONTAL}
              allowNegative={false}
              min={0}
              max={100}
              value={padding.values[1]}
              onChange={padding.setCollapsedSecond}
            />
          </Container>
        )}
      />

      <BoxModelControl
        sectionLabel="Margin"
        expanded={margin.expanded}
        onToggleExpand={() => margin.setExpanded((prev) => !prev)}
        toggleIcon={ICONS.MARGIN_SIDES}
        toggleLabel="margin controls"
        values={margin.values}
        onChangeSlot={margin.setSlot}
        fields={[
          { slot: 0, icon: ICONS.MARGIN_TOP, ariaLabel: 'Margin top' },
          { slot: 1, icon: ICONS.MARGIN_RIGHT, ariaLabel: 'Margin right' },
          { slot: 2, icon: ICONS.MARGIN_BOTTOM, ariaLabel: 'Margin bottom' },
          { slot: 3, icon: ICONS.MARGIN_LEFT, ariaLabel: 'Margin left' },
        ]}
        renderCollapsed={() => (
          <Container className="flex gap-12 ai-flex-end">
            <NumberField
              label="Margin"
              icon={ICONS.MARGIN_VERTICAL}
              allowNegative={false}
              min={0}
              max={100}
              value={margin.values[0]}
              onChange={margin.setCollapsedFirst}
            />
            <NumberField
              icon={ICONS.MARGIN_HORIZONTAL}
              allowNegative={false}
              min={0}
              max={100}
              value={margin.values[1]}
              onChange={margin.setCollapsedSecond}
            />
          </Container>
        )}
      />
    </Container>
  );
}
